import { useState } from 'react';
import type { SocialPostGeneration } from '@/hooks/useSocialPostHistory';
import { SOCIAL_PLATFORMS } from '../types';

interface SocialPostHistoryPanelProps {
  history: SocialPostGeneration[];
  loading: boolean;
  onLoad: (item: SocialPostGeneration) => void;
  onDelete: (id: string) => void;
}

function formatDate(iso: string) {
  const d = new Date(iso);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function formatTime(iso: string) {
  const d = new Date(iso);
  return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
}

function getPlatformIcon(platform: string | null) {
  const found = SOCIAL_PLATFORMS.find((p) => p.id === platform);
  return found?.icon ?? 'ri-global-line';
}

export default function SocialPostHistoryPanel({
  history,
  loading,
  onLoad,
  onDelete,
}: SocialPostHistoryPanelProps) {
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const handleDeleteConfirm = async (id: string) => {
    setDeletingId(id);
    setDeleteConfirmId(null);
    await onDelete(id);
    setDeletingId(null);
  };

  if (loading) {
    return (
      <div className="space-y-2">
        {[1, 2, 3].map((i) => (
          <div key={i} className="bg-stone-50 rounded-xl border border-stone-100 p-3 animate-pulse">
            <div className="h-3 bg-stone-200 rounded w-2/3 mb-2" />
            <div className="h-2.5 bg-stone-100 rounded w-1/2" />
          </div>
        ))}
      </div>
    );
  }

  if (history.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-8 text-center px-4">
        <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-stone-100 mb-3">
          <i className="ri-history-line text-stone-400 text-lg" />
        </div>
        <p className="text-xs font-semibold text-stone-500 mb-1">No saved posts yet</p>
        <p className="text-xs text-stone-400 leading-relaxed">
          Generate a post or daily plan and it&apos;ll be saved here automatically.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {history.map((item) => {
        const isConfirming = deleteConfirmId === item.id;
        const isDeleting = deletingId === item.id;

        return (
          <div
            key={item.id}
            className={`group bg-white rounded-xl border border-stone-100 hover:border-orange-200 transition-all duration-200 overflow-hidden ${
              isDeleting ? 'opacity-40 pointer-events-none' : ''
            }`}
          >
            {isConfirming ? (
              <div className="flex items-center gap-2 px-3 py-3 bg-red-50 border-red-200">
                <i className="ri-error-warning-line text-red-500 text-sm shrink-0" />
                <span className="text-xs text-red-700 font-medium flex-1">
                  Delete this {item.generation_type === 'daily_content' ? 'saved plan' : 'post'}?
                </span>
                <button
                  onClick={() => handleDeleteConfirm(item.id)}
                  className="text-xs font-bold text-white bg-red-500 hover:bg-red-600 px-2.5 py-1 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
                >
                  Delete
                </button>
                <button
                  onClick={() => setDeleteConfirmId(null)}
                  className="text-xs text-stone-400 hover:text-stone-600 cursor-pointer"
                >
                  <i className="ri-close-line" />
                </button>
              </div>
            ) : (
              <div className="flex items-center gap-3 px-3 py-3">
                {/* Platform icon */}
                <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-orange-50 border border-orange-100 shrink-0">
                  <i className={`${getPlatformIcon(item.platform)} text-orange-500 text-sm`} />
                </div>

                {/* Info */}
                <div className="flex-1 min-w-0">
                  <div className="text-xs font-bold text-stone-800 truncate">
                    {item.business_name ?? 'Untitled'}
                  </div>
                  <div className="flex items-center gap-1.5 mt-0.5">
                    <span className="text-xs text-stone-400">{item.platform}</span>
                    <span className="text-stone-200">·</span>
                    <span className="text-xs text-stone-400">
                      {item.generation_type === 'daily_content'
                        ? `${item.plan_quantity ?? '1'}-day plan`
                        : item.post_type}
                    </span>
                    <span className="text-stone-200">·</span>
                    <span className="text-xs text-stone-400">{formatDate(item.created_at)}</span>
                  </div>
                  {item.generation_type === 'daily_content' && item.daily_posts && item.daily_posts.length > 0 ? (
                    <p className="text-xs text-stone-500 mt-1 line-clamp-1 leading-relaxed">
                      {item.daily_posts.length} saved post{item.daily_posts.length === 1 ? '' : 's'} · {item.daily_posts[0]?.postType ?? 'Daily plan'}
                    </p>
                  ) : item.caption1 ? (
                    <p className="text-xs text-stone-500 mt-1 line-clamp-1 leading-relaxed">
                      {item.caption1}
                    </p>
                  ) : null}
                </div>

                {/* Actions */}
                <div className="flex items-center gap-1 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button
                    onClick={() => onLoad(item)}
                    title="Load this post"
                    className="w-7 h-7 flex items-center justify-center rounded-lg bg-orange-500 text-white hover:bg-orange-600 transition-colors cursor-pointer"
                  >
                    <i className="ri-arrow-right-up-line text-xs" />
                  </button>
                  <button
                    onClick={() => setDeleteConfirmId(item.id)}
                    title="Delete"
                    className="w-7 h-7 flex items-center justify-center rounded-lg border border-stone-200 text-stone-400 hover:border-red-300 hover:text-red-500 hover:bg-red-50 transition-all cursor-pointer"
                  >
                    <i className="ri-delete-bin-line text-xs" />
                  </button>
                </div>

                {/* Time badge */}
                <span className="text-xs text-stone-300 shrink-0 group-hover:hidden">
                  {formatTime(item.created_at)}
                </span>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
